import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Bot,
  Settings,
  Shield,
  Bell,
  FileText,
  Download,
  Eye,
  Clock,
  Mail,
  Database,
  X,
  Trash2,
} from 'lucide-react';
import requests from '../../services/httpServices';

const meetingTypes = [
  { value: 'sales', label: 'Sales Call' },
  { value: 'coaching', label: 'Coaching Session' },
  { value: 'internal', label: 'Internal Meeting' },
  { value: 'webinar', label: 'Webinar' },
  { value: 'client_strategy', label: 'Client Strategy' },
  { value: 'podcast', label: 'Podcast/Content' },
  { value: 'other', label: 'Other' },
];

const retentionOptions = [
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '6 months' },
  { value: 365, label: '1 year' },
  { value: -1, label: 'Forever' },
];

const exportFormats = [
  { value: 'pdf', label: 'PDF Document' },
  { value: 'markdown', label: 'Markdown' },
  { value: 'json', label: 'JSON (API)' },
];

// Custom Toggle Switch
const Switch = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    className={`relative w-11 h-6 rounded-full transition-colors ${
      disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
    } ${checked ? 'bg-[#6a1fbf]' : 'bg-white/20'}`}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
);

// Custom Select
const Select = ({ value, onChange, options, placeholder }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#6a1fbf] transition-colors"
  >
    {placeholder && (
      <option value="" disabled>
        {placeholder}
      </option>
    )}
    {options.map((opt) => (
      <option key={opt.value} value={opt.value} className="bg-[#1a1a2e]">
        {opt.label}
      </option>
    ))}
  </select>
);

// Card component
const Card = ({ children, className = '' }) => (
  <div className={`bg-white/5 border border-white/10 rounded-xl ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children }) => (
  <div className="px-6 py-4 border-b border-white/10">{children}</div>
);

const CardTitle = ({ children, icon: Icon }) => (
  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
    {Icon && <Icon className="w-5 h-5 text-[#6a1fbf]" />}
    {children}
  </h3>
);

const CardContent = ({ children }) => (
  <div className="px-6 py-4 space-y-4">{children}</div>
);

// Setting Row component
const SettingRow = ({ label, description, children }) => (
  <div className="flex items-center justify-between py-2">
    <div className="flex-1 pr-4">
      <label className="text-white font-medium">{label}</label>
      {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
    </div>
    <div className="flex-shrink-0">{children}</div>
  </div>
);

// Badge component
const Badge = ({ children, onRemove }) => (
  <span className="inline-flex items-center gap-1 px-2 py-1 bg-white/10 rounded-md text-sm text-gray-300">
    {children}
    {onRemove && (
      <button onClick={onRemove} className="hover:text-red-400 ml-1">
        <X className="w-3 h-3" />
      </button>
    )}
  </span>
);

const defaultSettings = {
  enabled_by_default: true,
  auto_join_meetings: true,
  bot_display_name: 'AI Notetaker (Bot)',
  bot_visible_by_default: true,
  transcript_visible_to_participants: false,
  real_time_transcript_enabled: true,
  auto_detect_meeting_type: true,
  default_meeting_type: 'other',
  storage_settings: {
    retention_days: 90,
    auto_delete_after_export: false,
    encryption_enabled: true,
  },
  export_settings: {
    auto_email_summary: false,
    email_recipients: [],
    default_export_format: 'pdf',
  },
  notification_settings: {
    notify_on_completion: true,
    notify_on_action_items: true,
  },
};

export default function NotetakerSettingsTab() {
  const [settings, setSettings] = useState(defaultSettings);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [exportHistory, setExportHistory] = useState([]);

  // Load settings from backend
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true);
        const response = await requests.get('/notetaker/settings');
        if (response && response.settings) {
          setSettings({ ...defaultSettings, ...response.settings });
        }
        if (response && response.exportHistory) {
          setExportHistory(response.exportHistory);
        }
      } catch (error) {
        console.log('Using default settings (no saved settings found)');
        // Use default settings if none exist
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleChange = (path, value) => {
    setSettings((prev) => {
      const keys = path.split('.');
      const newSettings = { ...prev };
      let current = newSettings;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return newSettings;
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await requests.post('/notetaker/settings', { settings });
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const addEmailRecipient = () => {
    if (newEmail && !settings.export_settings.email_recipients.includes(newEmail)) {
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newEmail)) {
        toast.error('Please enter a valid email address');
        return;
      }
      handleChange('export_settings.email_recipients', [
        ...settings.export_settings.email_recipients,
        newEmail,
      ]);
      setNewEmail('');
    }
  };

  const removeEmailRecipient = (email) => {
    handleChange(
      'export_settings.email_recipients',
      settings.export_settings.email_recipients.filter((e) => e !== email)
    );
  };

  if (loading) {
    return (
      <div className="p-6 h-full flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#6a1fbf]"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <Bot className="w-6 h-6 text-[#6a1fbf]" />
          AI Notetaker Settings
        </h2>
        <p className="text-gray-400 mt-1">
          Configure how the AI notetaker behaves during your meetings
        </p>
      </div>

      {/* General Settings */}
      <Card>
        <CardHeader>
          <CardTitle icon={Settings}>General Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <SettingRow
            label="Enable by Default"
            description="Automatically enable notetaker for new meetings"
          >
            <Switch
              checked={settings.enabled_by_default}
              onChange={(v) => handleChange('enabled_by_default', v)}
            />
          </SettingRow>

          <SettingRow
            label="Auto-Join Meetings"
            description="Bot joins automatically when meeting starts"
          >
            <Switch
              checked={settings.auto_join_meetings}
              onChange={(v) => handleChange('auto_join_meetings', v)}
            />
          </SettingRow>

          <SettingRow
            label="Real-Time Transcript"
            description="Show live transcript to host during meeting"
          >
            <Switch
              checked={settings.real_time_transcript_enabled}
              onChange={(v) => handleChange('real_time_transcript_enabled', v)}
            />
          </SettingRow>

          <SettingRow
            label="Auto-Detect Meeting Type"
            description="AI determines meeting type for tailored insights"
          >
            <Switch
              checked={settings.auto_detect_meeting_type}
              onChange={(v) => handleChange('auto_detect_meeting_type', v)}
            />
          </SettingRow>

          <div className="space-y-2 py-2">
            <label className="text-white font-medium">Default Meeting Type</label>
            <Select
              value={settings.default_meeting_type}
              onChange={(v) => handleChange('default_meeting_type', v)}
              options={meetingTypes}
            />
          </div>

          <div className="space-y-2 py-2">
            <label className="text-white font-medium">Bot Display Name</label>
            <input
              type="text"
              value={settings.bot_display_name}
              onChange={(e) => handleChange('bot_display_name', e.target.value)}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#6a1fbf] transition-colors"
            />
          </div>
        </CardContent>
      </Card>

      {/* Privacy & Visibility */}
      <Card>
        <CardHeader>
          <CardTitle icon={Shield}>Privacy & Visibility</CardTitle>
        </CardHeader>
        <CardContent>
          <SettingRow
            label={
              <span className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                Show Bot in Participant List
              </span>
            }
            description="Bot appears as a participant"
          >
            <Switch
              checked={settings.bot_visible_by_default}
              onChange={(v) => handleChange('bot_visible_by_default', v)}
            />
          </SettingRow>

          <SettingRow
            label="Transcript Visible to Participants"
            description="Allow participants to see transcript availability"
          >
            <Switch
              checked={settings.transcript_visible_to_participants}
              onChange={(v) => handleChange('transcript_visible_to_participants', v)}
            />
          </SettingRow>
        </CardContent>
      </Card>

      {/* Storage & Retention */}
      <Card>
        <CardHeader>
          <CardTitle icon={Database}>Storage & Retention</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 py-2">
            <label className="text-white font-medium">Data Retention Period</label>
            <Select
              value={String(settings.storage_settings?.retention_days)}
              onChange={(v) => handleChange('storage_settings.retention_days', parseInt(v))}
              options={retentionOptions}
            />
          </div>

          <SettingRow label="Encryption" description="Store transcripts with encryption">
            <Switch
              checked={settings.storage_settings?.encryption_enabled}
              onChange={(v) => handleChange('storage_settings.encryption_enabled', v)}
            />
          </SettingRow>

          <SettingRow
            label="Auto-Delete After Export"
            description="Remove transcript after successful export"
          >
            <Switch
              checked={settings.storage_settings?.auto_delete_after_export}
              onChange={(v) => handleChange('storage_settings.auto_delete_after_export', v)}
            />
          </SettingRow>
        </CardContent>
      </Card>

      {/* Export Settings */}
      <Card>
        <CardHeader>
          <CardTitle icon={FileText}>Export Settings</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 py-2">
            <label className="text-white font-medium">Default Export Format</label>
            <Select
              value={settings.export_settings?.default_export_format}
              onChange={(v) => handleChange('export_settings.default_export_format', v)}
              options={exportFormats}
            />
          </div>

          <SettingRow
            label={
              <span className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                Auto-Email Summary
              </span>
            }
            description="Send summary email after meeting ends"
          >
            <Switch
              checked={settings.export_settings?.auto_email_summary}
              onChange={(v) => handleChange('export_settings.auto_email_summary', v)}
            />
          </SettingRow>

          {settings.export_settings?.auto_email_summary && (
            <div className="space-y-2 py-2">
              <label className="text-white font-medium">Email Recipients</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Add email address"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addEmailRecipient()}
                  className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-[#6a1fbf] transition-colors"
                />
                <button
                  onClick={addEmailRecipient}
                  className="px-4 py-2 bg-[#6a1fbf] hover:bg-[#5a1aaf] text-white text-sm rounded-lg transition-colors"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {settings.export_settings?.email_recipients?.map((email) => (
                  <Badge key={email} onRemove={() => removeEmailRecipient(email)}>
                    {email}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notifications */}
      <Card>
        <CardHeader>
          <CardTitle icon={Bell}>Notifications</CardTitle>
        </CardHeader>
        <CardContent>
          <SettingRow
            label="Notify on Completion"
            description="Get notified when notes are ready"
          >
            <Switch
              checked={settings.notification_settings?.notify_on_completion}
              onChange={(v) => handleChange('notification_settings.notify_on_completion', v)}
            />
          </SettingRow>

          <SettingRow
            label="Action Item Alerts"
            description="Get notified about action items assigned to you"
          >
            <Switch
              checked={settings.notification_settings?.notify_on_action_items}
              onChange={(v) => handleChange('notification_settings.notify_on_action_items', v)}
            />
          </SettingRow>
        </CardContent>
      </Card>

      {/* Export History */}
      {exportHistory.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle icon={Download}>Recent Exports</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-2 text-sm text-gray-400 font-medium">
                      Meeting
                    </th>
                    <th className="text-left py-3 px-2 text-sm text-gray-400 font-medium">
                      Date
                    </th>
                    <th className="text-left py-3 px-2 text-sm text-gray-400 font-medium">
                      Format
                    </th>
                    <th className="text-left py-3 px-2 text-sm text-gray-400 font-medium">
                      Size
                    </th>
                    <th className="text-right py-3 px-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {exportHistory.map((item) => (
                    <tr key={item.id} className="border-b border-white/5">
                      <td className="py-3 px-2 text-white">{item.meeting}</td>
                      <td className="py-3 px-2 text-gray-400">{item.date}</td>
                      <td className="py-3 px-2">
                        <span className="px-2 py-1 border border-white/20 rounded text-xs text-gray-300">
                          {item.format}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-gray-400">{item.size}</td>
                      <td className="py-3 px-2 text-right">
                        <button className="p-1.5 text-gray-400 hover:text-white transition-colors">
                          <Download className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Save Button */}
      <div className="flex justify-end pb-6">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2.5 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 text-white font-medium rounded-lg transition-opacity disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
}
